/**
 * Tam ekran — **kutuya** alınır, doğrudan `<video>`'ya değil.
 *
 * ## Sorun
 *
 * Altyazı bindirmesi (`.watch__subtitles`) `<video>`'nun **kardeşidir** ve
 * `position: absolute` ile onun üstüne oturur. Tarayıcıda tam ekran bir eleman
 * **en üst katmana (top layer)** taşır ve `::backdrop` ile ekranı kaplar;
 * katmana taşınmayan kardeşler **boyanmaz**.
 *
 * Gerçek sunucuda: altyazı küçük ekranda görünüyordu, tam ekrana geçince
 * **kayboluyordu**. Sebep altyazı değil, fullscreen hedefinin `<video>` olması.
 *
 * `position: fixed` bir `<body>` çocuğu da işe yaramaz — o da katmanın
 * arkasında, `::backdrop`'ın altında kalır.
 *
 * ## Çözüm
 *
 * Fullscreen hedefi, video'yu çevreleyen `.watch__stage` kutusudur. Kutu
 * video'yu **tam sarar** (tam ekranda da `width/height: auto` ile içeriğine
 * göre büzülür), böylece bindirme hem normalde hem tam ekranda video'nun
 * gerçek kutusuna göre konumlanır.
 *
 * Tarayıcının kendi tam ekran düğmesi `<video>` üzerinde çalışır ve
 * hedefi oraya sabitler; bu yüzden `controlsList` ile gizlenir ve yerine
 * aşağıdaki düğme konur. Desteklenmeyen tarayıcılarda (Safari) düğme
 * gizlenemeyebilir; o durumda `fullscreenchange` yakalanıp kutuya
 * **yeniden hedeflenir** (`resolveFullscreenAction`).
 *
 * ## Mobil hata: "tam ekrana girince küçük moda dönülmüyor"
 *
 * Belirti: sağ alttaki tam ekran düğmesine basınca video tam ekrana geçiyor,
 * **aynı düğmeye tekrar basınca** küçük pencere moduna dönülmüyor; kullanıcı
 * ancak kapatma düğmesiyle çıkabiliyor.
 *
 * Sebep iki ayrı yerde biriken bir hata sınıfı:
 *
 * 1. **Geç bağlanan öğe + `useEffect([ref])` deseni.**
 *    `WatchScreen` oynatma hazır değilken `<Preparing>` döndürür; `.watch__stage`
 *    ve `<video>` DOM'da **yoktur**. `useFullscreen` bu kancaları bir kez,
 *    mount anında okuyup **kapanır** (`stage = null`, `video = null`) ve etiket
 *    bir daha çalışmaz. Sonuç: `fullscreenchange` dinleyicisi "tam ekranda
 *    mıyım?" sorusunu **her zaman hayır** cevapladı — `isFullscreen` hiç
 *    `true` olmadı. Düğme "gir" yolunu seçti, tarayıcı tam ekrana geçti, ikinci
 *    basışta yine "gir" yolunu seçti (girmek zaten mümkün olmadığı için
 *    konsolda hata, ekranda hiçbir şey olmadı).
 *
 * 2. **Durumun `useState` ile tutulması.**
 *    `toggle` bir olay işleyicisinde çağrılır; `setIsFullscreen` render
 *    sonrasına kalır. Aynı karede ikinci bir `toggle` çağrısı **eskimiş**
 *    durumu görür. Bu yüzden "gir" ve "çık" kararı tek bir doğru kaynaktan
 *    (`document.fullscreenElement` + bir `ref`) okunmalı.
 *
 * Düzeltme: kanca **durum** (`element`) ile birlikte verilir, etiket
 * `[stage.element, video.element]` bağımlılıklarıyla yeniden kurulur ve
 * gerçek tam ekran durumu bir `ref`te tutulur (`modeRef`).
 */

import { Capacitor, SystemBars } from '@capacitor/core';
import type { RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { setImmersiveFullscreen } from './nativeUi.ts';

/**
 * Fullscreen durumunda ne yapılacağı — saf, DOM'suz.
 *
 * - hiçbiri fullscreen değil → `enter` (kutuya gir),
 * - kutu fullscreen'de → `exit`,
 * - `<video>` fullscreen'de (yerel düğmeye basıldı) → `retarget`
 *   (kutuya taşı; `null` = taşınamaz, olduğu gibi bırak).
 */
export type FullscreenAction = 'enter' | 'exit' | 'retarget';

export function resolveFullscreenAction(
  current: Element | null,
  video: Element | null,
  stage: Element | null,
): FullscreenAction | null {
  if (current === null) return stage === null ? null : 'enter';
  // Kutu zaten tam ekrandaysa kapat.
  if (current === stage) return 'exit';
  // Yerel düğme `<video>`'yu tam ekrana aldı: bindirme kardeş olduğu için
  // katmana çıkmadı. Tam ekranı kutuya taşı.
  if (current === video && stage !== null) return 'retarget';
  // Başka bir eleman tam ekrandaysa (ör. F11 benzeri durum) karışma.
  return 'exit';
}

/** Tam ekranın **hangi yolla** açık olduğu. */
type FullscreenMode = 'none' | 'native' | 'css';

interface FullscreenDocument extends Document {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => void;
}

interface LegacyFullscreenElement extends HTMLElement {
  webkitRequestFullscreen?: () => void;
}

interface LegacyVideoElement extends HTMLVideoElement {
  webkitEnterFullscreen?: () => void;
  webkitExitFullscreen?: () => void;
  webkitDisplayingFullscreen?: boolean;
}

/** Tarayıcının "şu an tam ekrandayız" dediği eleman (standart + webkit). */
function getFullscreenElement(): Element | null {
  const doc = document as FullscreenDocument;
  return doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
}

/** Standart tam ekran API'siyle çıkış. */
async function exitFullscreenApi(): Promise<void> {
  const doc = document as FullscreenDocument;
  if (!doc.fullscreenElement && !doc.webkitFullscreenElement) return;
  if (document.exitFullscreen) {
    await document.exitFullscreen();
    return;
  }
  doc.webkitExitFullscreen?.();
}

/** iOS'un yerel video tam ekranından çıkış. */
function exitVideoFullscreen(video: HTMLVideoElement | null): void {
  const vid = video as LegacyVideoElement | null;
  if (vid?.webkitDisplayingFullscreen) {
    vid.webkitExitFullscreen?.();
  }
}

/**
 * Tam ekrana giriş isteği.
 *
 * `onEntered`: giriş gerçekten oldu (standart API çözüldü ya da webkit
 * varyantı kabul edildi).
 * `onFallback`: tarayıcı hiçbir tam ekran API'sini desteklemiyor — çağıran
 * taraf CSS ile taklit edebilir.
 */
function requestFullscreenCompat(
  stage: HTMLElement | null,
  video: HTMLVideoElement | null,
  onFallback: () => void,
  onEntered: () => void,
): void {
  const stg = stage as LegacyFullscreenElement | null;
  const vid = video as LegacyVideoElement | null;

  if (stg?.requestFullscreen) {
    stg.requestFullscreen().then(
      () => onEntered(),
      () => {
        /*
         * Standart API reddetti. İki olasılık var: eleman belge içinde
         * değil (kutu sökülmüş) ya da zaten tam ekrandayız. İkisi de
         * "webkit varyantını dene" ile çözülür; olmazsa CSS yedeğine düşer.
         */
        if (stg.webkitRequestFullscreen) {
          try {
            stg.webkitRequestFullscreen();
            onEntered();
            return;
          } catch {
            // sıradaki denemeye geç
          }
        }
        if (vid?.webkitEnterFullscreen) {
          try {
            vid.webkitEnterFullscreen();
            onEntered();
            return;
          } catch {
            // sıradaki denemeye geç
          }
        }
        onFallback();
      },
    );
    return;
  }

  if (stg?.webkitRequestFullscreen) {
    try {
      stg.webkitRequestFullscreen();
      onEntered();
      return;
    } catch {
      // sıradaki denemeye geç
    }
  }

  if (vid?.webkitEnterFullscreen) {
    try {
      vid.webkitEnterFullscreen();
      onEntered();
      return;
    } catch {
      // sıradaki denemeye geç
    }
  }

  onFallback();
}

/** Geç bağlanan öğe: hem nesne referansı hem de "DOM'da" durumu. */
export interface FullscreenTarget<T extends HTMLElement> {
  /** Nesne referansı — olay anında okunur. */
  ref: RefObject<T | null>;
  /** Durum: öğe DOM'a girdiğinde değişir. */
  element: T | null;
}

export interface Fullscreen {
  /** Tam ekranda mı. */
  isFullscreen: boolean;
  /** Kendi düğmemiz: gir / çık. */
  toggle: () => void;
  /** Yerel düğme destekleniyorsa gizlenir; kendi düğmemiz görünür. */
  showButton: boolean;
}

export function useFullscreen(
  stage: FullscreenTarget<HTMLElement>,
  video: FullscreenTarget<HTMLVideoElement>,
): Fullscreen {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showButton, setShowButton] = useState(true);

  /**
   * Tam ekran **modu** React durumundan ayrı tutulur.
   *
   * Neden? `toggle` bir olay işleyicisinde çağrılır ve `setIsFullscreen`
   * render sonrasına kalır. Durumdan okunsaydı, aynı karedeki ikinci bir
   * `toggle` **eskimiş** değeri görürdü ("gir" de, "gir" de) — düğmeye basıp
   * bırakınca tam ekrana girip çıkıp yeniden girerdi. Gerçek durum
   * `document.fullscreenElement`'ten okunur; bu `ref` yalnız "hangi moddayız"
   * bilgisini tutar (yerel API / CSS taklidi).
   */
  const modeRef = useRef<FullscreenMode>('none');

  const stageElement = stage.element;
  const videoElement = video.element;

  /**
   * Android WebView'da HTML5 fullscreen yalnız WebView katmanını büyütür;
   * status/navigation bar sistem düzeyinde kalır. Capacitor 8'in core
   * `SystemBars` API'si bu iki çubuğu birlikte gizler. Web'de no-op'tur.
   *
   * Cleanup, oynatıcıdan çıkarken veya rota kapanırken çubukların gizli
   * kalmasını engeller.
   */
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let cancelled = false;
    const apply = (): void => {
      if (cancelled) return;
      void (isFullscreen ? SystemBars.hide() : SystemBars.show()).catch(() => undefined);
      void setImmersiveFullscreen(isFullscreen);
    };

    apply();
    /*
     * HTML5 fullscreen geçişi sistem çubuklarını geri getirebiliyor. Geçiş
     * tamamlandıktan sonra iki kez daha uygula; bildirim gölgesi hariç durum
     * ve navigasyon çubukları gizli kalsın.
     */
    const retries = isFullscreen ? [200, 700].map((ms) => setTimeout(apply, ms)) : [];

    return () => {
      cancelled = true;
      for (const timer of retries) clearTimeout(timer);
      if (isFullscreen) {
        void SystemBars.show().catch(() => undefined);
        void setImmersiveFullscreen(false);
      }
    };
  }, [isFullscreen]);

  useEffect(() => {
    const stageEl = stageElement;
    const videoEl = videoElement;

    const onChange = (): void => {
      const current = getFullscreenElement();
      const action = resolveFullscreenAction(current, videoEl, stageEl);

      if (action === 'retarget') {
        /*
         * Yerel düğme `<video>`'yu tam ekrana aldı; bindirme kardeş olduğu
         * için katmana çıkmadı. Tam ekranı kutuya taşı.
         */
        modeRef.current = 'none';
        setIsFullscreen(false);
        void exitFullscreenApi()
          .then(() => {
            const stg = stageEl as LegacyFullscreenElement | null;
            return stg?.requestFullscreen?.() ?? stg?.webkitRequestFullscreen?.();
          })
          .catch(() => undefined);
        return;
      }

      const active = current !== null && (current === stageEl || current === videoEl);
      modeRef.current = active ? 'native' : 'none';
      setIsFullscreen(active);
    };

    // iOS: `<video>`'nun **yerel** tam ekranı (belge tam ekranı değil).
    const onIosBegin = (): void => {
      modeRef.current = 'native';
      setIsFullscreen(true);
    };
    const onIosEnd = (): void => {
      modeRef.current = 'none';
      setIsFullscreen(false);
    };

    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);

    const vid = videoEl as LegacyVideoElement | null;
    if (vid?.addEventListener) {
      vid.addEventListener('webkitbeginfullscreen', onIosBegin);
      vid.addEventListener('webkitendfullscreen', onIosEnd);
    }

    setShowButton(true);

    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
      if (vid?.removeEventListener) {
        vid.removeEventListener('webkitbeginfullscreen', onIosBegin);
        vid.removeEventListener('webkitendfullscreen', onIosEnd);
      }
    };
    /*
     * Bağımlılık **öğelerin kendisi**: kutu ve video geç bağlandığı için
     * mount anında `null` olabilir. `[stage.ref]` deseni boş kalır ve
     * `fullscreenchange` dinleyicisi hiç bağlanmazdı (bkz. `useElementRef`).
     */
  }, [stageElement, videoElement]);

  const toggle = useCallback(() => {
    const stageEl = stage.ref.current;
    const videoEl = video.ref.current;
    const current = getFullscreenElement();

    // 1) CSS ile taklit edilen tam ekran → kapat.
    if (modeRef.current === 'css') {
      modeRef.current = 'none';
      setIsFullscreen(false);
      return;
    }

    // 2) Gerçek tam ekran (kutu ya da video) → kapat.
    if (current !== null) {
      modeRef.current = 'none';
      setIsFullscreen(false);
      void exitFullscreenApi().catch(() => undefined);
      exitVideoFullscreen(videoEl);
      return;
    }

    // 3) iOS'un yerel video tam ekranı → kapat.
    const vid = videoEl as LegacyVideoElement | null;
    if (vid?.webkitDisplayingFullscreen) {
      modeRef.current = 'none';
      setIsFullscreen(false);
      exitVideoFullscreen(videoEl);
      return;
    }

    if (stageEl === null && videoEl === null) return;

    // 4) Gir.
    requestFullscreenCompat(
      stageEl,
      videoEl,
      () => {
        // Tarayıcı tam ekranı desteklemiyor: CSS ile taklit et.
        modeRef.current = 'css';
        setIsFullscreen(true);
      },
      () => {
        modeRef.current = 'native';
        setIsFullscreen(true);
      },
    );
  }, [stage.ref, video.ref]);

  return { isFullscreen, toggle, showButton };
}
